// src/services/callFeed.js — live call-history records + the missed-call badge.
//
// The Calls panel's rows come from the phone's export, which only refreshes on a
// full history sync — a call that just happened wouldn't appear until then. The
// server, however, broadcasts a canonical 'call-history-sync' record to every
// device of both parties at the end of every call (the same record the phones
// write to SQLite). This module ingests those live, keeps them in localStorage,
// and derives the unread badge: missed incoming calls newer than the last time
// the user actually opened the Calls screen.
import { getSocket } from './socket';
import { rememberCallRow, callRowFrom } from './localCallRows';

const KEY = 'allo_call_feed';
const SEEN_KEY = 'allo_calls_seen_at';

let records = [];
try { records = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch {}
const subs = new Set();
let attached = null;

function save() { try { localStorage.setItem(KEY, JSON.stringify(records.slice(0, 200))); } catch {} }
function notify() { for (const f of subs) { try { f([...records]); } catch {} } }

function norm(rec, id) {
  let parts = [];
  try { parts = typeof rec.participants === 'string' ? JSON.parse(rec.participants) : (rec.participants || []); } catch {}
  return {
    id,
    callType: rec.callType === 'video' ? 'video' : 'voice',
    outcome: String(rec.outcome || 'missed'),
    callInitiatedBy: String(rec.callInitiatedBy || ''),
    participants: parts,
    duration: Number(rec.duration) || 0,
    ts: Number(rec.eventTime) || Date.now(),
    handledOnWeb: String(rec.handledOnWeb || '') === '1',
    /* ⭐ WHO IT WAS, KEPT RATHER THAN LOOKED UP. The Calls panel used to name the
       other party from the chat document — and a guest CALL link creates no chat,
       so every guest call read "Allo user". Worse, the obvious fix (read
       users/{uid}) stops working too: a guest's account is deleted the moment the
       link expires. The server puts the names on the record; this keeps them. */
    fromName: String(rec.fromName || ''),
    toName: String(rec.toName || ''),
    /* '' when neither side was a guest */
    guestUid: String(rec.guestUid || ''),
    /* 🟥 THE CHAT THIS CALL BELONGS TO, WHICH WAS ON THE WIRE ALL ALONG AND
       DROPPED HERE. The server puts `chatId` on every record (index.js ~1342);
       normalising it away left the Calls panel to DERIVE one, as
       `[myUid, peer].sort().join('_')`. That happens to be right for a 1:1 and
       is wrong for anything else: a group call has many participants, so the
       derivation picks an arbitrary member, builds a one-to-one id that names a
       different conversation, and the row ends up wearing that member's face
       and name instead of the group's.
       🟢 `isGroup` is carried beside it rather than inferred from the
       participant count: two people in a group is a real group, and a call that
       happened to have two participants is not evidence either way. */
    chatId: String(rec.chatId || ''),
    isGroup: String(rec.isGroup || '') === '1' || rec.isGroup === true,
  };
}

/** Wire the socket listener (idempotent per socket — call after login). */
export function attachCallFeed() {
  const s = getSocket();
  if (!s || s === attached) return;
  attached = s;
  s.on('call-history-sync', (rec) => {
    const id = String(rec?.callSessionId || rec?.callId || '');
    if (!id) return;
    const fresh = norm(rec, id);

    /* 🟥 A GROUP CALL ALSO LEAVES A ROW IN THE CONVERSATION, and the browser has
       to keep its own copy of it. The status message written to
       `chats/{id}/messages` is a RELAY for the phones — ChatModal ingests it
       into SQLite and then deletes it, including documents it did not write —
       so the browser's only copy was being swept out from under it seconds
       after it appeared. This is the browser's SQLite. It also means a browser
       that merely took part in a call gets the row, without having hosted it. */
    /* 🟥 THIS USED TO READ `fresh.isGroup && fresh.chatId`, so only GROUP calls
       were kept — and the comment above, about the browser's only copy being
       swept out from under it, is exactly as true of a one-to-one call. It has
       no local row at all, so its bubble lives only in the Firestore document
       that store-and-forward deletes seconds later and in the phone's next
       export. Appearing and then vanishing until a re-sync is not a different
       bug from the group one; it is the same bug, never finished. */
    if (fresh.chatId) {
      const row = callRowFrom({
        chatId: fresh.chatId,
        sessionId: id,
        callType: fresh.callType,
        duration: fresh.duration,
        callInitiatedBy: fresh.callInitiatedBy,
        starterName: rec.starterName || fresh.fromName || '',
        participants: fresh.participants,
        startedFrom: rec.startedFrom,
        at: fresh.ts,
        isGroup: !!fresh.isGroup,
        /* 🟢 carried rather than assumed — missed and declined are most of what a
           one-to-one row has to say. */
        outcome: fresh.outcome,
      });
      if (row) rememberCallRow(fresh.chatId, row);
    }

    const i = records.findIndex((r) => r.id === id);
    if (i >= 0) records[i] = { ...records[i], ...fresh };
    else records.unshift(fresh);
    records = records.slice(0, 200);
    save();
    notify();
  });
}

export function subscribeCallFeed(fn) { subs.add(fn); fn([...records]); return () => subs.delete(fn); }
export function getCallFeed() { return [...records]; }

export function callsSeenAt() { return Number(localStorage.getItem(SEEN_KEY) || 0); }
/** Opening the Calls screen shows the whole list — that IS reading it. */
export function markCallsSeen() {
  try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch {}
  notify(); // wake badge subscribers so the count recomputes
}

/** Missed incoming calls (feed + export rows) newer than the last visit. */
export function missedUnseenCount(feedRecords, syncedHistory, myUid) {
  const seen = callsSeenAt();
  const counted = new Set();
  let n = 0;
  for (const r of feedRecords || []) {
    if (r.outcome !== 'missed' || !r.callInitiatedBy || r.callInitiatedBy === myUid) continue;
    if (r.ts <= seen) continue;
    counted.add(r.id); n += 1;
  }
  for (const msgs of Object.values(syncedHistory || {})) {
    for (const m of msgs || []) {
      const cd = m && m.callData;
      if (!cd || !cd.callType) continue;
      const sid = String(cd.callSessionId || '').replace(/^call_end_/, '') || String(m.id || '').replace(/^call_end_/, '');
      if (!sid || counted.has(sid)) continue;
      const incoming = cd.callInitiatedBy && cd.callInitiatedBy !== myUid;
      const missed = cd.outcome === 'missed' || (!cd.answered && cd.outcome !== 'declined' && incoming);
      const ts = typeof m.timestamp === 'number' ? m.timestamp : 0;
      if (incoming && missed && ts > seen) { counted.add(sid); n += 1; }
    }
  }
  return n;
}
